// ============================================================================
// trail.js — Masamune's swing ribbon. Each frame during an attack we push the
// blade's two world-space endpoints; the ribbon is the strip swept between the
// last N samples, glowing ember at the leading edge and fading to a dark core
// along its length (additive so it reads against the ashen field).
// ============================================================================

import * as THREE from 'three';

const N = 22; // ribbon samples

const TRAIL_VERT = `
  attribute float aT;
  varying float vT;
  void main(){
    vT = aT;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }`;
const TRAIL_FRAG = `
  varying float vT;              // 0 = tail (old), 1 = leading edge (new)
  void main(){
    float a = vT * vT;           // fade tail out
    vec3 col = mix(vec3(0.12,0.05,0.14), vec3(1.0,0.45,0.12), vT);
    gl_FragColor = vec4(col * (0.4 + vT), a * 0.9);
  }`;

export class SwordTrail {
  constructor(scene) {
    this.samples = []; // {tx,ty,tz,bx,by,bz}
    const g = new THREE.BufferGeometry();
    this.pos = new Float32Array(N * 2 * 3);
    this.tAttr = new Float32Array(N * 2);
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aT', new THREE.BufferAttribute(this.tAttr, 1).setUsage(THREE.DynamicDrawUsage));
    const idx = [];
    for (let i = 0; i < N - 1; i++) {
      const a = i * 2, b = i * 2 + 1, c = i * 2 + 2, d = i * 2 + 3;
      idx.push(a, b, c, b, d, c);
    }
    g.setIndex(idx);
    g.setDrawRange(0, 0);
    const m = new THREE.ShaderMaterial({
      vertexShader: TRAIL_VERT, fragmentShader: TRAIL_FRAG,
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
  }

  // top/bot are THREE.Vector3 world positions of the blade ends this frame
  update(active, top, bot) {
    if (active) {
      this.samples.push({ tx: top.x, ty: top.y, tz: top.z, bx: bot.x, by: bot.y, bz: bot.z });
      if (this.samples.length > N) this.samples.shift();
    } else if (this.samples.length > 0) {
      this.samples.shift(); // retract the tail when not swinging
    }

    const s = this.samples;
    const count = s.length;
    if (count < 2) { this.mesh.geometry.setDrawRange(0, 0); return; }

    for (let i = 0; i < count; i++) {
      const smp = s[i];
      const o = i * 6;
      this.pos[o] = smp.tx; this.pos[o + 1] = smp.ty; this.pos[o + 2] = smp.tz;
      this.pos[o + 3] = smp.bx; this.pos[o + 4] = smp.by; this.pos[o + 5] = smp.bz;
      const t = i / (count - 1);
      this.tAttr[i * 2] = t; this.tAttr[i * 2 + 1] = t;
    }
    const g = this.mesh.geometry;
    g.attributes.position.needsUpdate = true;
    g.attributes.aT.needsUpdate = true;
    g.setDrawRange(0, (count - 1) * 6);
  }
}
